import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Star, ShieldCheck, MapPin, Languages, Clock } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function ProfessionalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [book, setBook] = useState({ session_type: "consultation", duration_hours: 1, scheduled_at: "", notes: "" });

  useEffect(() => {
    api.get(`/professionals/${id}`).then((r) => setData(r.data));
  }, [id]);

  if (!data) return <div className="max-w-4xl mx-auto p-12 text-[#94A3B8]">Loading…</div>;
  const p = data.profile;

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const handleBook = async () => {
    if (!user) return navigate("/login");
    if (user.role !== "client") {
      toast.error("Only client accounts can book. Switch to client from your menu.");
      return;
    }
    if (!book.scheduled_at) {
      toast.error("Please pick a date & time");
      return;
    }
    setBusy(true);
    try {
      // 1. Create booking (pending_payment)
      const { data: booking } = await api.post("/bookings", {
        professional_id: id,
        session_type: book.session_type,
        scheduled_at: new Date(book.scheduled_at).toISOString(),
        duration_hours: Number(book.duration_hours),
        notes: book.notes,
      });
      // 2. Create Razorpay order
      const { data: order } = await api.post("/payments/razorpay/order", {
        booking_id: booking.booking_id,
      });

      if (order.mocked || !order.key_id) {
        // No Razorpay keys — auto confirm (mock)
        await api.post("/payments/razorpay/verify", {
          booking_id: booking.booking_id,
          razorpay_order_id: order.order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: "mock",
        });
        toast.success("Booked (test mode — no real payment)");
        navigate("/dashboard");
        return;
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Failed to load Razorpay checkout");

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "2ndInnings",
        description: `Session with ${data.profile.name}`,
        order_id: order.order_id,
        prefill: { name: user.name, email: user.email },
        theme: { color: "#2DD4BF" },
        handler: async (res) => {
          try {
            await api.post("/payments/razorpay/verify", {
              booking_id: booking.booking_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful! Booking confirmed.");
            navigate("/dashboard");
          } catch (err) {
            toast.error("Payment verification failed. Contact support.");
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled. Your booking is saved as pending in dashboard.");
            setBusy(false);
          },
        },
      };
      const rz = new window.Razorpay(options);
      rz.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Booking failed");
    } finally {
      setBusy(false);
    }
  };

  const total = p.hourly_rate * (Number(book.duration_hours) || 1);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-start gap-6 mb-8">
            <img src={p.picture || "https://images.unsplash.com/photo-1607746882042-944635dfe10e?crop=entropy&cs=srgb&fm=jpg&h=400&w=400"}
                 alt="" className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-sm" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-display text-3xl md:text-4xl text-[#E8ECF3]">{p.name}</h1>
                {p.verified && (
                  <span className="inline-flex items-center gap-1 text-xs bg-[#2DD4BF] text-white rounded-full px-3 py-1">
                    <ShieldCheck className="h-3 w-3" /> Verified
                  </span>
                )}
              </div>
              <div className="text-[#94A3B8] mt-2 max-w-xl">{p.headline}</div>
              <div className="flex flex-wrap items-center gap-5 mt-4 text-sm text-[#94A3B8]">
                <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" /> {p.avg_rating || "New"} · {p.reviews_count || 0} reviews</span>
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {p.location}</span>
                <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {p.years_experience}+ yrs</span>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <section>
              <div className="label-eyebrow text-[#2DD4BF] mb-3">About</div>
              <p className="text-[#E8ECF3] leading-relaxed whitespace-pre-line">{p.bio}</p>
            </section>
            <section>
              <div className="label-eyebrow text-[#2DD4BF] mb-3">Expertise</div>
              <div className="flex flex-wrap gap-2">
                {(p.expertise || []).map((e) => (
                  <span key={e} className="bg-[#0F1B33] text-[#E8ECF3] rounded-full px-4 py-1.5 text-sm">{e}</span>
                ))}
              </div>
            </section>
            <section>
              <div className="label-eyebrow text-[#2DD4BF] mb-3">Languages</div>
              <div className="flex items-center gap-2 text-[#E8ECF3]">
                <Languages className="h-4 w-4 text-[#2DD4BF]" />
                {(p.languages || []).join(" · ")}
              </div>
            </section>
            <section>
              <div className="label-eyebrow text-[#2DD4BF] mb-4">Reviews</div>
              {(!data.reviews || data.reviews.length === 0) ? (
                <div className="text-[#94A3B8]">No reviews yet — be the first to book & review.</div>
              ) : (
                <div className="space-y-4">
                  {data.reviews.map((r) => (
                    <div key={r.review_id} className="border border-[#142542] rounded-2xl p-5 bg-[#0F1B33]">
                      <div className="flex items-center justify-between">
                        <div className="font-display text-lg text-[#E8ECF3]">{r.client_name}</div>
                        <div className="flex items-center gap-1 text-sm">
                          <Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" /> {r.rating}
                        </div>
                      </div>
                      <div className="text-[#94A3B8] mt-2">{r.comment}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 border border-[#142542] rounded-3xl p-8 bg-[#0F1B33]">
            <div className="font-display text-4xl text-[#E8ECF3]">
              ₹{p.hourly_rate}<span className="text-base text-[#94A3B8]">/hr</span>
            </div>
            <div className="text-sm text-[#94A3B8] mt-1">Platform fee 12% · Rest to the expert</div>
            <Dialog>
              <DialogTrigger asChild>
                <Button className="w-full mt-6 h-12 rounded-full bg-[#2DD4BF] hover:bg-[#14B8A6] text-white" data-testid="open-booking-dialog">
                  Book a session
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Book with {p.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Session type</Label>
                    <Select value={book.session_type} onValueChange={(v) => setBook({ ...book, session_type: v })}>
                      <SelectTrigger data-testid="booking-session-type" className="mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="consultation">One-off Consultation</SelectItem>
                        <SelectItem value="mentorship">Mentorship</SelectItem>
                        <SelectItem value="project">Short Project</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Duration (hours)</Label>
                    <Input type="number" min={1} max={40} value={book.duration_hours}
                           onChange={(e) => setBook({ ...book, duration_hours: e.target.value })}
                           className="mt-2" data-testid="booking-duration" />
                  </div>
                  <div>
                    <Label>Scheduled at</Label>
                    <Input type="datetime-local" value={book.scheduled_at}
                           onChange={(e) => setBook({ ...book, scheduled_at: e.target.value })}
                           className="mt-2" data-testid="booking-scheduled" />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea rows={3} value={book.notes}
                              onChange={(e) => setBook({ ...book, notes: e.target.value })}
                              className="mt-2" placeholder="Tell them what you'd like help with…" data-testid="booking-notes" />
                  </div>
                  <div className="p-4 rounded-2xl bg-[#0F1B33] text-sm">
                    <div className="flex justify-between"><span>Total</span><span className="font-display text-lg">₹{total}</span></div>
                    <div className="text-xs text-[#94A3B8] mt-1">Payment mocked for MVP · Real payments coming in v2</div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleBook} disabled={busy} className="w-full h-11 rounded-full bg-[#2DD4BF] hover:bg-[#14B8A6] text-[#05070C]" data-testid="confirm-booking">
                    {busy ? "Processing…" : `Pay ₹${total} & Book`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <div className="mt-6 text-xs text-[#94A3B8]">
              Response usually within 12 hours · Zoom link shared post booking
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
